<template>
  <div class="space-y-6">
    <!-- Weekly Overview (header + selectors + 5 stat cards) -->
    <PipelineWeeklyOverview
      :won-deals="wonDeals"
      :all-deals="allDeals"
      :selected-week="selectedWeek"
      @select-week="selectedWeek = $event"
    />

    <!-- Divider -->
    <div class="border-t-2 border-gray-200 my-8"></div>

    <!-- Monthly Filter Section -->
    <div>
      <h2 class="text-xl font-semibold mb-2">Vunna affärer per månad</h2>
      <p class="text-sm text-gray-600 mb-4">Välj månad för att se detaljerad månadsstatistik</p>
      <div class="bg-white rounded-lg shadow p-4 border-l-4" style="border-left-color: #3273DC">
        <div class="flex items-center gap-2 mb-3">
          <BaseIcon icon="calendar-alt" style="color: #3273DC" />
          <h3 class="text-base font-semibold">Välj månad</h3>
        </div>
        <select
          v-model="selectedMonth"
          class="w-full md:w-80 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option v-if="availableMonths.length === 0" value="" disabled>Inga månader tillgängliga</option>
          <option v-for="m in availableMonths" :key="m.key" :value="m.key">{{ m.label }}</option>
        </select>
      </div>
    </div>

    <!-- Current Month Stats (5 cards) -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #F1A239">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Värde {{ currentMonthName }}</p>
          <BaseIcon icon="dollar-sign" style="color: #F1A239" />
        </div>
        <p class="text-3xl font-semibold" style="color: #F1A239">{{ formatCurrency(currentMonthValue) }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #3273DC">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Antal affärer</p>
          <BaseIcon icon="trophy" style="color: #3273DC" />
        </div>
        <p class="text-3xl font-semibold" style="color: #3273DC">{{ formatNumber(currentMonthCount) }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2 border-gray-300">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Genomsnittsvärde</p>
          <BaseIcon icon="dollar-sign" class="text-gray-400" />
        </div>
        <p class="text-3xl font-semibold text-gray-900">{{ formatCurrency(currentMonthAverage) }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2 border-gray-300">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Förra månaden</p>
          <BaseIcon icon="calendar-alt" class="text-gray-400" />
        </div>
        <p class="text-3xl font-semibold text-gray-900">{{ formatCurrency(previousMonthValue) }}</p>
        <p v-if="previousMonthCount > 0" class="text-xs text-gray-500 mt-1">{{ previousMonthCount }} {{ previousMonthCount === 1 ? 'affär' : 'affärer' }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #3273DC">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Genomsn. Time to Close</p>
          <BaseIcon icon="chart-line" style="color: #3273DC" />
        </div>
        <p class="text-3xl font-semibold" style="color: #3273DC">
          {{ averageTimeToClose !== null ? averageTimeToClose : '-' }}
          <span v-if="averageTimeToClose !== null" class="text-base font-normal text-gray-500">dagar</span>
        </p>
      </div>
    </div>

    <!-- Target Fulfillment -->
    <div class="bg-white rounded-lg shadow p-6 border-l-4" style="border-left-color: #0F2C5D">
      <div class="flex items-center gap-2 mb-2">
        <BaseIcon icon="bullseye" class="text-lg" style="color: #0F2C5D" />
        <h3 class="text-lg font-semibold">Måluppfyllelse - {{ currentMonthName }}</h3>
      </div>
      <p class="text-sm text-gray-600 mb-4">Aktuellt värde mot månadsmål</p>
      <div class="space-y-4">
        <div class="flex items-center justify-between pb-4 border-b">
          <div class="text-sm font-medium text-gray-700">Månadsmål:</div>
          <div v-if="isEditingTarget" class="flex gap-2">
            <input type="number" v-model="targetInput" placeholder="Ange mål" class="border rounded px-2 py-1 w-32 text-sm" @keyup.enter="saveTarget" />
            <button class="px-2 py-1 bg-blue-600 text-white rounded text-sm" @click="saveTarget">
              <BaseIcon icon="check" />
            </button>
            <button class="px-2 py-1 border rounded text-sm" @click="isEditingTarget = false">
              <BaseIcon icon="times" />
            </button>
          </div>
          <div v-else class="flex items-center gap-2">
            <div class="text-lg font-semibold" style="color: #0F2C5D">{{ monthlyTarget > 0 ? formatCurrency(monthlyTarget) : 'Ej satt' }}</div>
            <button class="p-1 hover:bg-gray-100 rounded" @click="startEditTarget">
              <BaseIcon icon="edit" class="text-gray-500" />
            </button>
          </div>
        </div>
        <template v-if="monthlyTarget > 0">
          <div class="flex items-center justify-between text-sm">
            <div class="flex flex-col gap-1">
              <span class="text-gray-600">{{ formatCurrency(currentMonthValue) }} av {{ formatCurrency(monthlyTarget) }}</span>
              <span v-if="lastWeekValue > 0" class="text-green-600 font-semibold bg-green-50 px-2 py-1 rounded inline-flex items-center gap-1 w-auto text-xs">
                <BaseIcon icon="chart-line" class="text-xs" />
                Senaste veckan: {{ formatCurrency(lastWeekValue) }} ({{ lastWeekCount }} {{ lastWeekCount === 1 ? 'affär' : 'affärer' }})
              </span>
            </div>
            <span class="font-semibold text-lg">{{ targetFulfillment.toFixed(1) }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-6">
            <div class="h-6 rounded-full transition-all" :style="{ width: Math.min(targetFulfillment, 100) + '%', backgroundColor: '#0F2C5D' }"></div>
          </div>
          <div class="flex items-center justify-between text-sm">
            <span v-if="currentMonthValue >= monthlyTarget" class="text-green-600 font-semibold">✓ Mål uppnått!</span>
            <span v-else class="text-gray-600">Återstår: {{ formatCurrency(monthlyTarget - currentMonthValue) }}</span>
            <span v-if="targetFulfillment > 100" class="text-green-600 font-semibold">+{{ (targetFulfillment - 100).toFixed(1) }}% över mål</span>
          </div>
        </template>
        <div v-else class="text-center text-gray-500 py-4">Sätt ett månadsmål för att se måluppfyllelse</div>
      </div>
    </div>

    <!-- Top 3 Sellers Podium -->
    <div class="bg-white rounded-lg shadow p-6" style="background: linear-gradient(to bottom right, #F9FAFB, #FFFFFF)">
      <div class="flex items-center gap-2 mb-2">
        <BaseIcon icon="trophy" class="text-lg" style="color: #F1A239" />
        <h3 class="text-lg font-semibold">Månadens bästa säljare - {{ currentMonthName }}</h3>
      </div>
      <p class="text-sm text-gray-600 mb-6">Topp 3 säljare baserat på värde av stängda affärer</p>

      <div v-if="topSellers.length === 0" class="text-center text-gray-500 py-8">
        <BaseIcon icon="trophy" class="text-4xl mb-4 opacity-50" />
        <p>Inga vunna affärer för innevarande månad</p>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Silver (2nd) -->
        <div v-if="topSellers[1]" class="flex flex-col items-center order-2 md:order-1">
          <div class="w-24 h-24 rounded-full flex items-center justify-center mb-4 shadow-lg" style="background: linear-gradient(to bottom right, #D1D5DB, #9CA3AF)">
            <BaseIcon icon="medal" class="text-3xl text-white" />
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-gray-600 mb-1">2</div>
            <div class="text-lg font-semibold text-gray-900 mb-2">{{ topSellers[1].name }}</div>
            <div class="text-2xl font-bold text-gray-700 mb-1">{{ formatCurrency(topSellers[1].value) }}</div>
            <div class="text-sm text-gray-500">{{ topSellers[1].count }} {{ topSellers[1].count === 1 ? 'affär' : 'affärer' }}</div>
          </div>
        </div>

        <!-- Gold (1st) -->
        <div v-if="topSellers[0]" class="flex flex-col items-center order-1 md:order-2">
          <div class="w-32 h-32 rounded-full flex items-center justify-center mb-4 shadow-2xl ring-4 ring-yellow-200" style="background: linear-gradient(to bottom right, #FDE047, #FACC15, #EAB308)">
            <BaseIcon icon="trophy" class="text-5xl text-white" />
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-yellow-600 mb-1">1</div>
            <div class="text-xl font-bold text-gray-900 mb-2">{{ topSellers[0].name }}</div>
            <div class="text-3xl font-bold mb-1" style="color: #F1A239">{{ formatCurrency(topSellers[0].value) }}</div>
            <div class="text-sm text-gray-600">{{ topSellers[0].count }} {{ topSellers[0].count === 1 ? 'affär' : 'affärer' }}</div>
          </div>
        </div>

        <!-- Bronze (3rd) -->
        <div v-if="topSellers[2]" class="flex flex-col items-center order-3">
          <div class="w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg" style="background: linear-gradient(to bottom right, #FDBA74, #FB923C)">
            <BaseIcon icon="award" class="text-3xl text-white" />
          </div>
          <div class="text-center">
            <div class="text-xl font-bold text-orange-600 mb-1">3</div>
            <div class="text-lg font-semibold text-gray-900 mb-2">{{ topSellers[2].name }}</div>
            <div class="text-xl font-bold text-orange-700 mb-1">{{ formatCurrency(topSellers[2].value) }}</div>
            <div class="text-sm text-gray-500">{{ topSellers[2].count }} {{ topSellers[2].count === 1 ? 'affär' : 'affärer' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div v-if="monthlySales.length > 0" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Monthly Sales Value -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-1">Värde av stängda affärer per månad</h3>
        <p class="text-sm text-gray-600 mb-4">Historisk utveckling av försäljningsvärde</p>
        <canvas ref="monthlySalesChart" height="300"></canvas>
      </div>

      <!-- Monthly Average -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-1">Genomsnittligt affärsvärde per månad</h3>
        <p class="text-sm text-gray-600 mb-4">Genomsnittligt värde per affär varje månad</p>
        <canvas ref="monthlyAverageChart" height="300"></canvas>
      </div>
    </div>

    <!-- Seller Distribution Chart -->
    <div v-if="sellerDistribution.length > 0" class="bg-white rounded-lg shadow p-6 border-l-4" style="border-left-color: #3273DC">
      <div class="flex items-center gap-2 mb-1">
        <BaseIcon icon="users" style="color: #3273DC" />
        <h3 class="text-lg font-semibold">Fördelning per säljare - {{ currentMonthName }}</h3>
      </div>
      <p class="text-sm text-gray-600 mb-4">Värde av vunna affärer per säljare under månaden (klicka på stapel för detaljer)</p>
      <canvas ref="sellerDistributionChart" height="110"></canvas>
    </div>

    <!-- Daily Sales Chart -->
    <div v-if="dailySales.length > 0" class="bg-white rounded-lg shadow p-4">
      <h3 class="text-base font-semibold mb-1">Värde av stängda affärer per dag - {{ currentMonthName }}</h3>
      <p class="text-sm text-gray-600 mb-2">Daglig försäljning för innevarande månad</p>
      <canvas ref="dailySalesChart" height="120"></canvas>
    </div>

    <!-- Top 10 Deals Table -->
    <div v-if="top10Deals.length > 0" class="bg-white rounded-lg shadow p-6">
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2">Topp 10 största affärerna - {{ currentMonthName }}</h3>
        <p class="text-sm text-gray-600">
          De 10 största affärerna av totalt {{ currentMonthCount }} stängda {{ currentMonthCount === 1 ? 'affär' : 'affärer' }} &bull;
          Topp 10 utgör {{ top10Percentage.toFixed(1) }}% av totalt värde ({{ formatCurrency(top10Value) }} av {{ formatCurrency(currentMonthValue) }})
        </p>
      </div>      <div class="overflow-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b">
              <th class="text-left p-3">#</th>
              <th class="text-left p-3">Säljare</th>
              <th class="text-left p-3">Företag</th>
              <th class="text-left p-3">Datum</th>
              <th class="text-right p-3">Värde</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(deal, index) in top10Deals" :key="index" class="border-b hover:bg-gray-50" :class="index < 3 ? 'bg-yellow-50' : ''">
              <td class="p-3">
                <div class="flex items-center gap-2">
                  <span v-if="index === 0">🥇</span>
                  <span v-else-if="index === 1">🥈</span>
                  <span v-else-if="index === 2">🥉</span>
                  <span v-else class="text-gray-500 font-medium w-8 text-center">{{ index + 1 }}</span>
                </div>
              </td>
              <td class="p-3 font-medium">{{ deal['Säljare'] || 'Okänd' }}</td>
              <td class="p-3">{{ deal['Företag'] || 'Okänt' }}</td>
              <td class="p-3">{{ deal['Avslutsdatum'] ? new Date(deal['Avslutsdatum']).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' }) : '-' }}</td>
              <td class="text-right p-3 font-bold" style="color: #F1A239">{{ formatCurrency(deal['Ordervärde'] || 0) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 bg-gray-50">
              <td colspan="4" class="p-3 font-bold text-right">Topp 10 totalt ({{ top10Percentage.toFixed(1) }}% av totalt värde):</td>
              <td class="text-right p-3 font-bold text-lg" style="color: #F1A239">{{ formatCurrency(top10Value) }}</td>
            </tr>
            <tr class="bg-gray-100">
              <td colspan="4" class="p-3 font-bold text-right">Totalt alla affärer:</td>
              <td class="text-right p-3 font-bold text-gray-700 text-lg">{{ formatCurrency(currentMonthValue) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Seller Deals Modal -->
    <div v-if="isSellerModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" @click.self="closeSellerModal">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between p-4 border-b">
          <div>
            <h3 class="text-2xl font-semibold">{{ selectedSeller }} - {{ currentMonthName }}</h3>
            <p class="text-sm text-gray-600 mt-1">
              {{ selectedSellerDeals.length }} {{ selectedSellerDeals.length === 1 ? 'affär' : 'affärer' }} med totalt värde
              {{ formatCurrency(selectedSellerTotal) }}
            </p>
          </div>
          <button class="text-gray-400 hover:text-gray-600" @click="closeSellerModal">
            <BaseIcon icon="times" />
          </button>
        </div>
        <div class="p-4 overflow-x-auto">
          <table v-if="selectedSellerDeals.length > 0" class="w-full">
            <thead>
              <tr class="border-b">
                <th class="text-left p-3">Företag</th>
                <th class="text-left p-3">Beskrivning</th>
                <th class="text-right p-3 whitespace-nowrap">Ordervärde</th>
                <th class="text-left p-3 whitespace-nowrap">Skapad</th>
                <th class="text-left p-3 whitespace-nowrap">Avslutad</th>
                <th class="text-right p-3 whitespace-nowrap">Time to Close</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(deal, idx) in selectedSellerDeals" :key="`sd-${idx}`" class="border-b hover:bg-gray-50">
                <td class="p-3">{{ deal['Företag'] || '-' }}</td>
                <td class="p-3 max-w-md">{{ deal['Beskrivning'] || '-' }}</td>
                <td class="text-right p-3 font-semibold whitespace-nowrap" style="color: #F1A239">
                  {{ formatCurrency(deal['Ordervärde'] || 0) }}
                </td>
                <td class="p-3 whitespace-nowrap">{{ formatDateShort(getCreatedDate(deal)) }}</td>
                <td class="p-3 whitespace-nowrap">{{ formatDateShort(deal['Avslutsdatum']) }}</td>
                <td class="text-right p-3 whitespace-nowrap font-semibold" style="color: #3273DC">
                  {{ getTimeToClose(deal) !== null ? `${getTimeToClose(deal)} dagar` : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-8 text-gray-500">Inga vunna affärer för denna säljare</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js";
import PipelineWeeklyOverview from "./PipelineWeeklyOverview.vue";

export default {
  name: "PipelineWonDeals",
  components: { PipelineWeeklyOverview },
  props: {
    wonDeals: { type: Array, required: true },
    allDeals: { type: Array, default: null },
  },
  data() {
    return {
      monthlyTarget: 0,
      isEditingTarget: false,
      targetInput: "",
      selectedWeek: "",
      selectedMonth: this.defaultMonthKey(),
      isSellerModalOpen: false,
      selectedSeller: null,
      charts: {},
    };
  },
  computed: {
    availableMonths() {
      const map = {};
      this.wonDeals.forEach((deal) => {
        if (!deal.Avslutsdatum) return;
        try {
          const d = new Date(deal.Avslutsdatum);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          if (!map[key]) {
            map[key] = {
              key,
              label: d.toLocaleDateString("sv-SE", { year: "numeric", month: "long" }),
            };
          }
        } catch { /* skip */ }
      });
      return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
    },
    selectedMonthDate() {
      const [y, m] = (this.selectedMonth || this.defaultMonthKey()).split("-").map(Number);
      return { year: y, month: m - 1 };
    },
    currentMonthName() {
      const { year, month } = this.selectedMonthDate;
      return new Date(year, month, 1).toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
    },
    currentMonthData() {
      const { year, month } = this.selectedMonthDate;
      return this.wonDeals.filter((deal) => {
        if (!deal.Avslutsdatum) return false;
        try {
          const d = new Date(deal.Avslutsdatum);
          return d.getMonth() === month && d.getFullYear() === year;
        } catch { return false; }
      });
    },
    currentMonthValue() {
      return this.currentMonthData.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
    },
    currentMonthCount() {
      return this.currentMonthData.length;
    },
    currentMonthAverage() {
      return this.currentMonthCount > 0 ? this.currentMonthValue / this.currentMonthCount : 0;
    },
    previousMonthData() {
      const { year, month } = this.selectedMonthDate;
      const prev = new Date(year, month - 1, 1);
      const prevYear = prev.getFullYear();
      const prevMonth = prev.getMonth();
      return this.wonDeals.filter((deal) => {
        if (!deal.Avslutsdatum) return false;
        try {
          const d = new Date(deal.Avslutsdatum);
          return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
        } catch { return false; }
      });
    },
    previousMonthValue() {
      return this.previousMonthData.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
    },
    previousMonthCount() {
      return this.previousMonthData.length;
    },
    averageTimeToClose() {
      const valid = this.currentMonthData.filter((deal) => this.getCreatedDate(deal) && deal["Avslutsdatum"]);
      if (valid.length === 0) return null;
      const totalDays = valid.reduce((sum, deal) => {
        const days = this.getTimeToClose(deal);
        return sum + (days !== null && days >= 0 ? days : 0);
      }, 0);
      return Math.round(totalDays / valid.length);
    },
    targetFulfillment() {
      return this.monthlyTarget > 0 ? (this.currentMonthValue / this.monthlyTarget) * 100 : 0;
    },
    lastWeekData() {
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return this.currentMonthData.filter((deal) => {
        if (!deal.Avslutsdatum) return false;
        try {
          const d = new Date(deal.Avslutsdatum);
          return d >= oneWeekAgo && d <= now;
        } catch { return false; }
      });
    },
    lastWeekValue() {
      return this.lastWeekData.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
    },
    lastWeekCount() {
      return this.lastWeekData.length;
    },
    topSellers() {
      const sellerTotals = {};
      this.currentMonthData.forEach((deal) => {
        const seller = deal["Säljare"] || "Okänd";
        if (!sellerTotals[seller]) sellerTotals[seller] = { name: seller, value: 0, count: 0 };
        sellerTotals[seller].value += Number(deal["Ordervärde"]) || 0;
        sellerTotals[seller].count += 1;
      });
      return Object.values(sellerTotals).sort((a, b) => b.value - a.value).slice(0, 3);
    },
    sellerDistribution() {
      const sellerTotals = {};
      this.currentMonthData.forEach((deal) => {
        const seller = deal["Säljare"] || "Okänd";
        if (!sellerTotals[seller]) sellerTotals[seller] = { name: seller, value: 0, count: 0 };
        sellerTotals[seller].value += Number(deal["Ordervärde"]) || 0;
        sellerTotals[seller].count += 1;
      });
      return Object.values(sellerTotals).sort((a, b) => b.value - a.value);
    },
    selectedSellerDeals() {
      if (!this.selectedSeller) return [];
      return this.currentMonthData
        .filter((deal) => (deal["Säljare"] || "Okänd") === this.selectedSeller)
        .sort((a, b) => (Number(b["Ordervärde"]) || 0) - (Number(a["Ordervärde"]) || 0));
    },
    selectedSellerTotal() {
      return this.selectedSellerDeals.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
    },
    monthlySales() {
      const monthlyData = {};
      this.wonDeals.forEach((deal) => {
        if (!deal.Avslutsdatum) return;
        try {
          const date = new Date(deal.Avslutsdatum);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
          const monthName = date.toLocaleDateString("sv-SE", { year: "numeric", month: "short" });
          if (!monthlyData[monthKey]) monthlyData[monthKey] = { key: monthKey, month: monthName, value: 0, count: 0, average: 0 };
          monthlyData[monthKey].value += Number(deal["Ordervärde"]) || 0;
          monthlyData[monthKey].count += 1;
        } catch { /* skip invalid dates */ }
      });
      Object.values(monthlyData).forEach((d) => { d.average = d.count > 0 ? d.value / d.count : 0; });

      // 12 months back from selected
      const { year, month } = this.selectedMonthDate;
      const selectedDate = new Date(year, month, 1);
      const startDate = new Date(year, month - 12, 1);
      return Object.values(monthlyData)
        .sort((a, b) => a.key.localeCompare(b.key))
        .filter((d) => {
          const [y, m] = d.key.split("-").map(Number);
          const md = new Date(y, m - 1, 1);
          return md > startDate && md <= selectedDate;
        });
    },
    dailySales() {
      const { year, month } = this.selectedMonthDate;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const dailyData = {};
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        dailyData[dayKey] = { day: date.toLocaleDateString("sv-SE", { month: "short", day: "numeric" }), value: 0, count: 0 };
      }
      this.currentMonthData.forEach((deal) => {
        if (!deal.Avslutsdatum) return;
        try {
          const date = new Date(deal.Avslutsdatum);
          const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
          if (dailyData[dayKey]) {
            dailyData[dayKey].value += Number(deal["Ordervärde"]) || 0;
            dailyData[dayKey].count += 1;
          }
        } catch { /* skip */ }
      });
      return Object.entries(dailyData).sort(([a], [b]) => a.localeCompare(b)).map(([, d]) => d);
    },
    top10Deals() {
      return [...this.currentMonthData].sort((a, b) => (Number(b["Ordervärde"]) || 0) - (Number(a["Ordervärde"]) || 0)).slice(0, 10);
    },
    top10Value() {
      return this.top10Deals.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
    },
    top10Percentage() {
      return this.currentMonthValue > 0 ? (this.top10Value / this.currentMonthValue) * 100 : 0;
    },
  },
  watch: {
    wonDeals: {
      handler() { this.$nextTick(() => this.renderCharts()); },
      deep: true,
    },
    selectedMonth() {
      this.$nextTick(() => this.renderCharts());
    },
    availableMonths: {
      immediate: true,
      handler(val) {
        if (val.length === 0) return;
        if (!val.some((m) => m.key === this.selectedMonth)) {
          this.selectedMonth = val[0].key;
        }
      },
    },
  },
  mounted() {
    this.loadTarget();
    this.$nextTick(() => this.renderCharts());
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  methods: {
    defaultMonthKey() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    },
    getCreatedDate(deal) {
      return deal["Skapad datum"] || deal["Skapad"] || deal["col_10"] || null;
    },
    getTimeToClose(deal) {
      const created = this.getCreatedDate(deal);
      if (!created || !deal["Avslutsdatum"]) return null;
      try {
        const c = new Date(created);
        const closed = new Date(deal["Avslutsdatum"]);
        return Math.round((closed.getTime() - c.getTime()) / (1000 * 60 * 60 * 24));
      } catch { return null; }
    },
    formatDateShort(date) {
      if (!date) return "-";
      try { return new Date(date).toLocaleDateString("sv-SE"); } catch { return "-"; }
    },
    formatNumber(num) {
      return Number(num).toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    formatCurrency(num) {
      return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
    },
    async loadTarget() {
      try {
        const stored = localStorage.getItem("won_deals_monthly_target");
        if (stored) this.monthlyTarget = Number(stored);
      } catch { /* ignore */ }
    },
    startEditTarget() {
      this.targetInput = this.monthlyTarget.toString();
      this.isEditingTarget = true;
    },
    saveTarget() {
      const value = Number(String(this.targetInput).replace(/\s/g, ""));
      if (!isNaN(value) && value >= 0) {
        this.monthlyTarget = value;
        localStorage.setItem("won_deals_monthly_target", value.toString());
        this.isEditingTarget = false;
      }
    },
    destroyCharts() {
      Object.values(this.charts).forEach((c) => { if (c) c.destroy(); });
      this.charts = {};
    },
    renderCharts() {
      this.destroyCharts();
      this.renderMonthlySalesChart();
      this.renderMonthlyAverageChart();
      this.renderSellerDistributionChart();
      this.renderDailySalesChart();
    },
    openSellerModal(name) {
      this.selectedSeller = name;
      this.isSellerModalOpen = true;
    },
    closeSellerModal() {
      this.isSellerModalOpen = false;
      this.selectedSeller = null;
    },
    renderSellerDistributionChart() {
      if (this.sellerDistribution.length === 0) return;
      const el = this.$refs.sellerDistributionChart;
      if (!el) return;
      const labels = this.sellerDistribution.map((d) => d.name);
      const values = this.sellerDistribution.map((d) => d.value);
      const counts = this.sellerDistribution.map((d) => d.count);
      this.charts.sellerDistributionChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: [{ label: "Värde (SEK)", data: values, backgroundColor: "#3273DC" }],
        },
        options: {
          responsive: true,
          onClick: (_evt, elements) => {
            if (elements && elements.length > 0) {
              const idx = elements[0]._index;
              if (idx !== undefined && labels[idx]) this.openSellerModal(labels[idx]);
            }
          },
          scales: { yAxes: [{ ticks: { callback: (v) => this.formatNumber(v) } }] },
          tooltips: {
            callbacks: {
              label: (item) => `Värde: ${this.formatCurrency(item.yLabel)}`,
              afterLabel: (item) => {
                const c = counts[item.index];
                return [`Antal: ${c} ${c === 1 ? "affär" : "affärer"}`, "Klicka för detaljer"];
              },
            },
          },
        },
      });
    },
    renderMonthlySalesChart() {
      if (this.monthlySales.length === 0) return;
      const el = this.$refs.monthlySalesChart;
      if (!el) return;
      this.charts.monthlySalesChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.monthlySales.map((d) => d.month),
          datasets: [{ label: "Värde (SEK)", data: this.monthlySales.map((d) => d.value), backgroundColor: "#3273DC" }],
        },
        options: {
          responsive: true,
          scales: { yAxes: [{ ticks: { callback: (v) => this.formatNumber(v) } }] },
          tooltips: {
            callbacks: {
              label: (item) => `Värde: ${this.formatCurrency(item.yLabel)}`,
              afterLabel: (item) => {
                const d = this.monthlySales[item.index];
                return [`Antal: ${d.count} st`, `Genomsnitt: ${this.formatCurrency(d.average)}`];
              },
            },
          },
        },
      });
    },
    renderMonthlyAverageChart() {
      if (this.monthlySales.length === 0) return;
      const el = this.$refs.monthlyAverageChart;
      if (!el) return;
      this.charts.monthlyAverageChart = new Chart(el.getContext("2d"), {
        type: "line",
        data: {
          labels: this.monthlySales.map((d) => d.month),
          datasets: [{
            label: "Genomsnittlig affär (SEK)",
            data: this.monthlySales.map((d) => d.average),
            borderColor: "#0F2C5D",
            borderWidth: 4,
            fill: false,
            pointBackgroundColor: "#0F2C5D",
            pointRadius: 6,
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          scales: { yAxes: [{ ticks: { callback: (v) => this.formatNumber(v) } }] },
          tooltips: {
            callbacks: {
              label: (item) => {
                const d = this.monthlySales[item.index];
                return `Genomsnitt: ${this.formatCurrency(d.average)}`;
              },
              afterLabel: (item) => {
                const d = this.monthlySales[item.index];
                return `Antal: ${d.count} st`;
              },
            },
          },
        },
      });
    },
    renderDailySalesChart() {
      if (this.dailySales.length === 0) return;
      const el = this.$refs.dailySalesChart;
      if (!el) return;
      this.charts.dailySalesChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.dailySales.map((d) => d.day),
          datasets: [{ label: "Värde (SEK)", data: this.dailySales.map((d) => d.value), backgroundColor: "#F1A239" }],
        },
        options: {
          responsive: true,
          scales: { yAxes: [{ ticks: { callback: (v) => this.formatNumber(v) } }] },
          tooltips: {
            callbacks: {
              label: (item) => `Värde: ${this.formatCurrency(item.yLabel)}`,
              afterLabel: (item) => {
                const d = this.dailySales[item.index];
                return `Antal: ${d.count} st`;
              },
            },
          },
        },
      });
    },
  },
};
</script>
